// TNB commercial e-bill parser (2026-07-24). Feed the flattened pdf.js text
// (page items joined by spaces) and it pulls the fields the CSC needs:
//   • tariff / voltage    — "Voltan Rendah | Sederhana | Tinggi" → LV/MV/HV (+ default rate)
//   • ToU flag            — "ToU" / "Puncak"
//   • monthly usage       — "Purata Penggunaan Anda : N kWh" (6-month average)
//   • monthly bill        — "Purata Jumlah Bil : RM N" (avg), else average the (BS) RM lines
//   • maximum demand (MD) — the meter table's kW row; ToU shows kW P / kW O → take the highest
// Every field is best-effort; the caller keeps them editable.

const TARIFF_RATE = { LV: 0.515, MV: 0.303, HV: 0.20 };

const nn = (s) => (s == null ? null : parseFloat(String(s).replace(/,/g, '')));

export function parseTnbBill(text) {
    const T = (text || '').replace(/\s+/g, ' ');
    const grab = (re) => { const m = T.match(re); return m ? m[1] : null; };

    // ── Tariff / voltage ──
    let voltage = null;
    if (/Voltan\s*Rendah/i.test(T)) voltage = 'LV';
    else if (/Voltan\s*Sederhana/i.test(T)) voltage = 'MV';
    else if (/Voltan\s*Tinggi/i.test(T)) voltage = 'HV';
    const isTou = /\bToU\b|Puncak/i.test(T);
    const tariffRate = voltage ? TARIFF_RATE[voltage] : null;

    // ── Monthly usage (6-month average) ──
    const monthlyUsage = nn(grab(/Purata\s*Penggunaan\s*Anda\s*:?\s*([\d,]+)\s*kWh/i));

    // ── Monthly bill: prefer the printed average, else average the (BS) RM lines ──
    let monthlyBill = nn(grab(/Purata\s*Jumlah\s*Bil\s*:?\s*RM\s*([\d,]+\.?\d*)/i));
    if (monthlyBill == null) {
        const bs = [...T.matchAll(/\(BS\)\s*RM\s*([\d,]+\.?\d*)/gi)].map((m) => nn(m[1])).filter((x) => x > 0);
        if (bs.length) monthlyBill = Math.round((bs.reduce((s, x) => s + x, 0) / bs.length) * 100) / 100;
    }
    // last-resort current-bill figures
    const currentBill = nn(grab(/Amaun\s*:?\s*RM\s*([\d,]+\.?\d*)/i))
        ?? nn(grab(/Jumlah\s*Bil\s*Anda\s*\(RM\)\s*([\d,]+\.?\d*)/i));

    // ── Maximum demand from the meter table ──
    // pdf.js renders each meter row in order, so "<value> kW" (space, NOT kWh/kVAR) is the
    // demand read. Restrict to the meter region so charge lines like "(RM29.43/kW)" or
    // "745.00kW" (no space) never match. ToU → kW P + kW O → take the max.
    const region = ((T.split(/Maklumat\s*Meter/i)[1] || T).split(/Informasi|Nota\s*:/i)[0]) || T;
    const mdVals = [...region.matchAll(/([\d,]+)\s+kW(?!h)\s*([PO])?\b/gi)].map((m) => nn(m[1])).filter((x) => x > 0);
    const md = mdVals.length ? Math.max(...mdVals) : null;

    // ── ToU split (Medium Voltage): the meter table prints "86,258 kWh P" / "118,565 kWh O" ──
    // The MV bill is calculated from peak kWh + off-peak kWh + MD, so pull both. (user 2026-09-23)
    const peakKwh = nn((region.match(/([\d,]+)\s+kWh\s*P\b/i) || [])[1]);
    const offKwh = nn((region.match(/([\d,]+)\s+kWh\s*O\b/i) || [])[1]);

    const filled = ['voltage', 'monthlyUsage', 'monthlyBill', 'md']
        .filter((k) => ({ voltage, monthlyUsage, monthlyBill, md }[k]) != null).length;

    /* ── AFA, in sen/kWh, SIGNED ──
       The bill prints its own rate for its own billing period, on a line like
         "AFA (254,138kWh, -RM0.0277/kWh) mulai 01/02/26"
       and on this account every recent month is a REBATE: Feb −2.77 sen, Mar −2.15,
       Apr −0.47. Reading it matters twice over — without it the calculated bill cannot
       reach TNB's Caj Semasa (RM 155,154 on that February bill, RM 7,040 of it AFA), and
       the rate also sits inside mvSolarRate(), so it moves what every saved kWh is worth.
       Left to the General Settings default the tool applies THIS month's national rate to
       a bill from another month, with the wrong sign.
       Returned in SEN because that is the unit setAfaRate()/getAfaRate() speak, and the
       minus is kept: a rebate that arrives as a surcharge is worse than no rate at all.
       (user 2026-10-31: "i upload the latest tnb bill, can it read the AFA too?") */
    const afaRm = grab(/AFA\s*\([^)]*?(-?\s*RM\s*[\d.]+)\s*\/\s*kWh/i);
    let afaSen = null;
    if (afaRm != null) {
        const v = parseFloat(String(afaRm).replace(/\s|RM/gi, ''));
        if (Number.isFinite(v)) afaSen = Math.round(v * 100 * 100) / 100;   // RM/kWh → sen/kWh
    }

    return { voltage, tariffRate, isTou, monthlyUsage, monthlyBill, currentBill, md, peakKwh, offKwh, afaSen, filled };
}
